import { useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import {
  CredentialValidationError,
  fetchAppsflyerApps,
  saveCredentials,
  type AppsflyerApp,
} from '../../api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

type Step = 'token' | 'select' | 'manual';

/** Platforms we model. AppsFlyer's `platform` is an open string, so unknown
 * values normalize to "other" (the raw value is preserved server-side). */
const APPSFLYER_PLATFORMS = [
  'ios', 'android', 'windows', 'web', 'roku', 'fire_tv', 'samsung_tv', 'lg_tv', 'other',
] as const;
type AppsflyerPlatform = (typeof APPSFLYER_PLATFORMS)[number];

const PLATFORM_LABELS: Record<AppsflyerPlatform, string> = {
  ios: 'iOS',
  android: 'Android',
  windows: 'Windows',
  web: 'Web',
  roku: 'Roku',
  fire_tv: 'Fire TV',
  samsung_tv: 'Samsung TV',
  lg_tv: 'LG TV',
  other: 'Other',
};

const normalizePlatform = (raw: string | null): AppsflyerPlatform => {
  const value = (raw ?? '').trim().toLowerCase();
  return (APPSFLYER_PLATFORMS as readonly string[]).includes(value)
    ? (value as AppsflyerPlatform)
    : 'other';
};

/** One app posted to the credentials save (matches the backend's normalize_apps). */
type ConnectApp = {
  app_id: string;
  platform: string;
  name?: string | null;
  currency?: string | null;
  time_zone?: string | null;
};

type ManualApp = { appId: string; platform: AppsflyerPlatform };

interface AppsFlyerCredentialModalProps {
  platformName: string;
  onClose: () => void;
  onConnected: () => void;
}

/**
 * Connect modal for AppsFlyer (mirrors mvp's AppsFlyerConnectionModal).
 * Enter the V2.0 API token, then multi-select the apps fetched from the
 * Management API — grouped by platform (iOS, Android, Windows, Web, CTV…). Falls
 * back to manual `{ app id, platform }` entry if listing fails or returns
 * nothing. Submits the selected apps through the standard credentials save
 * (which validates the token and enriches currency/time zone server-side).
 */
export default function AppsFlyerCredentialModal({
  platformName,
  onClose,
  onConnected,
}: AppsFlyerCredentialModalProps) {
  const [step, setStep] = useState<Step>('token');
  const [bearerToken, setBearerToken] = useState('');
  const [apps, setApps] = useState<AppsflyerApp[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [manualApps, setManualApps] = useState<ManualApp[]>([{ appId: '', platform: 'ios' }]);
  const [error, setError] = useState('');
  const [errorHint, setErrorHint] = useState('');
  const [loadingApps, setLoadingApps] = useState(false);
  const [saving, setSaving] = useState(false);

  const busy = loadingApps || saving;

  const clearErrors = () => {
    setError('');
    setErrorHint('');
  };

  const handleFetchApps = async () => {
    if (!bearerToken.trim()) {
      setError('Please enter your AppsFlyer API token');
      return;
    }
    setLoadingApps(true);
    clearErrors();
    try {
      const { apps: fetched } = await fetchAppsflyerApps(bearerToken.trim());
      if (fetched.length === 0) {
        setError('No apps were found for this token. Enter the app IDs manually.');
        setStep('manual');
        return;
      }
      setApps(fetched);
      // Nothing selected by default — the user opts in per app.
      setSelectedIds([]);
      setSearchQuery('');
      setStep('select');
    } catch (err) {
      if (err instanceof CredentialValidationError) {
        // Invalid token — keep the user on the token step to fix it.
        setError(err.message);
        return;
      }
      // Listing unavailable (e.g. token lacks Management-API scope) — fall
      // back to manual entry so the user can still connect.
      setError(
        (err instanceof Error ? err.message : 'Could not load your apps.') +
          ' You can enter the app IDs manually.',
      );
      setStep('manual');
    } finally {
      setLoadingApps(false);
    }
  };

  const connect = async (payload: ConnectApp[]) => {
    setSaving(true);
    clearErrors();
    try {
      await saveCredentials('appsflyer', { bearer_token: bearerToken.trim(), apps: payload });
      onConnected();
      onClose();
    } catch (err) {
      if (err instanceof CredentialValidationError) {
        setError('Incorrect credentials');
        setErrorHint(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to connect AppsFlyer');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSelection = () => {
    const selected = apps.filter((app) => selectedIds.includes(app.id));
    if (selected.length === 0) {
      setError('Select at least one app');
      return;
    }
    void connect(
      selected.map((app) => ({
        app_id: app.id,
        platform: app.platform ?? '',
        name: app.name,
        currency: app.currency,
        time_zone: app.time_zone,
      })),
    );
  };

  const handleSaveManual = () => {
    const cleaned = manualApps
      .map((row) => ({ appId: row.appId.trim(), platform: row.platform }))
      .filter((row) => row.appId !== '');
    if (cleaned.length === 0) {
      setError('Provide at least one app ID');
      return;
    }
    void connect(cleaned.map((row) => ({ app_id: row.appId, platform: row.platform })));
  };

  const toggleApp = (id: string) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  // Name search over the select step (matches the id shown as the subtitle too).
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredApps = normalizedQuery
    ? apps.filter((app) => (app.name || app.id).toLowerCase().includes(normalizedQuery))
    : apps;

  const visibleIds = filteredApps.map((app) => app.id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const toggleAll = () =>
    setSelectedIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds])),
    );

  const updateManualApp = (index: number, patch: Partial<ManualApp>) =>
    setManualApps((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const addManualApp = () =>
    setManualApps((prev) => [...prev, { appId: '', platform: 'ios' }]);
  const removeManualApp = (index: number) =>
    setManualApps((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));

  // Apps grouped by normalized platform, in APPSFLYER_PLATFORMS order.
  const groups = APPSFLYER_PLATFORMS.map((platform) => ({
    platform,
    items: filteredApps.filter((app) => normalizePlatform(app.platform) === platform),
  })).filter((group) => group.items.length > 0);

  const hasManualAppId = manualApps.some((row) => row.appId.trim() !== '');

  const subtitle =
    step === 'select'
      ? 'Choose the apps to connect.'
      : step === 'manual'
        ? 'Enter your AppsFlyer app IDs.'
        : 'Enter your AppsFlyer API token to load your apps.';

  const canSubmit = step === 'select' ? selectedIds.length > 0 : hasManualAppId;

  const selectClass =
    'h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm ' +
    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-60';

  const renderAppRow = (app: AppsflyerApp, selected: boolean, onSelect: () => void) => (
    <button
      key={app.id}
      type="button"
      onClick={onSelect}
      disabled={busy}
      className={cn(
        'flex w-full items-center gap-3 rounded-md border p-3 text-left transition-colors',
        selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50',
        busy && 'opacity-60',
      )}
    >
      <span
        className={cn(
          'flex size-4 shrink-0 items-center justify-center rounded border',
          selected ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40',
        )}
      >
        {selected && (
          <svg className="size-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">{app.name || app.id}</span>
        <span className="block truncate text-xs text-muted-foreground">
          {app.id}
          {app.currency ? ` · ${app.currency}` : ''}
          {app.time_zone ? ` · ${app.time_zone}` : ''}
        </span>
      </span>
    </button>
  );

  return (
    <Dialog open onOpenChange={(open: boolean) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect {platformName}</DialogTitle>
          <DialogDescription>{subtitle}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {step === 'token' && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="af-bearer-token">Pull API bearer token</Label>
                <Input
                  id="af-bearer-token"
                  type="password"
                  autoComplete="off"
                  placeholder="eyJhbGci..."
                  value={bearerToken}
                  disabled={busy}
                  onChange={(e) => setBearerToken(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleFetchApps();
                    }
                  }}
                />
              </div>

              <div className="rounded-md border bg-muted/50 p-3 text-xs text-muted-foreground">
                <p className="mb-1 font-medium text-foreground">How to get your token</p>
                <ol className="list-decimal space-y-0.5 pl-4">
                  <li>AppsFlyer → Account settings → API tokens</li>
                  <li>Copy the API V2.0 token</li>
                  <li>Fetch your apps and pick the ones to audit</li>
                </ol>
              </div>

              <button
                type="button"
                onClick={() => { clearErrors(); setStep('manual'); }}
                disabled={busy}
                className="text-xs text-muted-foreground underline disabled:opacity-50"
              >
                Or enter app IDs manually
              </button>
            </>
          )}

          {step === 'select' && (
            <>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  autoComplete="off"
                  placeholder="Search apps by name"
                  aria-label="Search apps by name"
                  value={searchQuery}
                  disabled={busy}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  {selectedIds.length} of {apps.length} selected
                </span>
                <button
                  type="button"
                  onClick={toggleAll}
                  disabled={busy || visibleIds.length === 0}
                  className="text-xs underline disabled:opacity-50"
                >
                  {allVisibleSelected ? 'Deselect all' : 'Select all'}
                </button>
              </div>
              <div className="h-[min(360px,45vh)] space-y-4 overflow-y-auto pr-1">
                {groups.map((group) => (
                  <div key={group.platform} className="space-y-1.5">
                    <Label>{PLATFORM_LABELS[group.platform]}</Label>
                    {group.items.map((app) =>
                      renderAppRow(app, selectedIds.includes(app.id), () => toggleApp(app.id)),
                    )}
                  </div>
                ))}
                {groups.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    {apps.length === 0
                      ? 'No apps were found for this token.'
                      : `No apps match “${searchQuery.trim()}”.`}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => { clearErrors(); setStep('manual'); }}
                disabled={busy}
                className="text-xs text-muted-foreground underline disabled:opacity-50"
              >
                Enter app IDs manually instead
              </button>
            </>
          )}

          {step === 'manual' && (
            <>
              {manualApps.map((row, index) => (
                <div key={index} className="flex items-end gap-2">
                  <div className="flex-1 space-y-1.5">
                    <Label htmlFor={`af-app-id-${index}`}>App ID</Label>
                    <Input
                      id={`af-app-id-${index}`}
                      type="text"
                      autoComplete="off"
                      placeholder="id123456789 or com.example.app"
                      value={row.appId}
                      disabled={busy}
                      onChange={(e) => updateManualApp(index, { appId: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`af-platform-${index}`}>Platform</Label>
                    <select
                      id={`af-platform-${index}`}
                      value={row.platform}
                      disabled={busy}
                      onChange={(e) =>
                        updateManualApp(index, { platform: e.target.value as AppsflyerPlatform })
                      }
                      className={selectClass}
                    >
                      {APPSFLYER_PLATFORMS.map((platform) => (
                        <option key={platform} value={platform}>
                          {PLATFORM_LABELS[platform]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeManualApp(index)}
                    disabled={busy || manualApps.length === 1}
                    aria-label="Remove app"
                  >
                    ✕
                  </Button>
                </div>
              ))}
              <button
                type="button"
                onClick={addManualApp}
                disabled={busy}
                className="text-xs underline disabled:opacity-50"
              >
                Add another app
              </button>
            </>
          )}

          {error && (
            <div className="space-y-1">
              <p className="text-sm font-medium text-destructive">{error}</p>
              {errorHint && <p className="text-xs text-muted-foreground">{errorHint}</p>}
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            {step !== 'token' && (
              <Button
                variant="outline"
                onClick={() => { clearErrors(); setStep('token'); }}
                disabled={busy}
              >
                Back
              </Button>
            )}
            {step === 'token' ? (
              <Button
                size="lg"
                className="font-semibold tracking-tight"
                onClick={handleFetchApps}
                disabled={busy || !bearerToken.trim()}
              >
                {loadingApps ? 'Loading...' : 'Fetch apps'}
              </Button>
            ) : (
              <Button
                size="lg"
                className="font-semibold tracking-tight"
                onClick={step === 'select' ? handleSaveSelection : handleSaveManual}
                disabled={busy || !canSubmit}
              >
                {saving ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  'Save & Connect'
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
